/**
 * ToggleIconSwitch
 * ----------------------------------------------------------------------------
 * Botão liga/desliga padronizado do sistema.
 *
 * A partir da v2.0.0 o componente renderiza o <Switch /> global (Radix), o
 * mesmo usado nas linhas de opção da aba "Veículos" da página Configurações:
 * trilha arredondada, polegar deslizante e verde emerald-600 quando ligado.
 * Assim não existe mais um botão liga/desliga "de ícone" em umas telas e um
 * "de trilha" em outras.
 *
 * A API é exatamente a mesma da v1 (checked / onCheckedChange / disabled / id /
 * className), então as telas que já usam o componente continuam funcionando
 * sem alteração — inclusive a associação <Label htmlFor>, que o Switch aceita.
 *
 * Props:
 *  - checked: estado atual (ligado/desligado)
 *  - onCheckedChange: callback ao alternar
 *  - disabled: desabilita o botão
 *  - id: id opcional (para associar com <Label htmlFor>)
 *  - labelOn / labelOff: dica de contexto exibida ao passar o mouse
 *  - className: ajuste pontual de espaçamento na linha
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-05 v2.0.0 - Leiaute alinhado ao padrão da aba "Veículos"
 *            (Configurações): o ícone ToggleRight/ToggleLeft é substituído pelo
 *            <Switch /> compartilhado. Os tooltips do Radix saíram porque em
 *            todas as telas o rótulo já aparece ao lado do botão; a dica dos
 *            textos labelOn/labelOff segue disponível via atributo title.
 *   RELEASE: 2026-08 v1.0.0 - Versão inicial no formato icon-button
 *            (ToggleRight / ToggleLeft), padrão das telas de Departamentos e
 *            Filas de Distribuição.
 */
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';

interface ToggleIconSwitchProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  id?: string;
  labelOn?: string;
  labelOff?: string;
  className?: string;
}

export function ToggleIconSwitch({
  checked,
  onCheckedChange,
  disabled = false,
  id,
  labelOn = 'Desativar',
  labelOff = 'Ativar',
  className,
}: ToggleIconSwitchProps) {
  return (
    <Switch
      id={id}
      checked={checked}
      disabled={disabled}
      onCheckedChange={onCheckedChange}
      title={checked ? labelOn : labelOff}
      className={cn('shrink-0', className)}
    />
  );
}
